import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Divider,
  Chip,
  TextField,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";

// Contexts and Components
import { useAuth } from "../../contexts/AuthContext";
import AddOrganizationWizard from "./AddOrganizationWizard";

const Header = ({ onMenuClick, sidebarOpen }) => {
  const { user, switchCompany, updateDeviceName, getActiveCompany } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDeviceName, setEditingDeviceName] = useState(false);
  const [deviceNameValue, setDeviceNameValue] = useState(
    user?.device_name || "",
  );
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState(null);
  const [addOrgWizardOpen, setAddOrgWizardOpen] = useState(false);

  const activeCompany = getActiveCompany();
  const companyMenuOpen = Boolean(companyMenuAnchor);

  const formatLastSignIn = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSaveDeviceName = () => {
    updateDeviceName(deviceNameValue);
    setEditingDeviceName(false);
  };

  const handleCancelDeviceName = () => {
    setDeviceNameValue(user?.device_name || "");
    setEditingDeviceName(false);
  };

  const isCompanyActive = (company) => {
    const activeCompanyId = user?.activeCompanyId;
    if (!company || !activeCompanyId) return false;
    return (
      String(company.id) === String(activeCompanyId) ||
      String(company.slug) === String(activeCompanyId)
    );
  };

  const handleCompanyClick = (companyId) => {
    if (String(companyId) === String(user?.activeCompanyId)) {
      setCompanyMenuAnchor(null);
      return;
    }
    switchCompany(companyId);
    setCompanyMenuAnchor(null);
  };

  const handleCompanyMenuOpen = (event) => {
    setCompanyMenuAnchor(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
  };

  const handleAddOrganization = () => {
    setCompanyMenuAnchor(null);
    setAddOrgWizardOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingDeviceName(false);
    setDeviceNameValue(user?.device_name || "");
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.default',
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar>
          <Tooltip title={sidebarOpen ? "Collapse menu" : "Expand menu"}>
            <IconButton
              onClick={onMenuClick}
              edge="start"
              sx={{ mr: 2, color: "text.secondary" }}
            >
              {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "text.primary" }}
            >
              StatusReport
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {activeCompany && (
              <Box
                onClick={handleCompanyMenuOpen}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 260,
                  maxWidth: 320,
                  height: 36,
                  px: 2,
                  cursor: "pointer",
                  borderRadius: 1.5,
                  border: "2px solid",
                  borderColor:
                    activeCompany.brandColors?.primary || "primary.main",
                  backgroundColor:
                    activeCompany.brandColors?.secondary || "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    opacity: 0.85,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: activeCompany.brandColors?.text || "text.primary",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  {activeCompany.name}
                </Typography>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    ml: 0.5,
                    color: activeCompany.brandColors?.text || "text.primary",
                  }}
                />
              </Box>
            )}

            {/* Company Dropdown Menu */}
            <Menu
              anchorEl={companyMenuAnchor}
              open={companyMenuOpen}
              onClose={handleCompanyMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 280,
                  maxWidth: 360,
                  mt: 0.5,
                  borderRadius: 1.5,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              {user?.companies?.map((company) => {
                const isActive = isCompanyActive(company);
                return (
                  <MenuItem
                    key={company.id}
                    disabled={isActive}
                    onClick={() => handleCompanyClick(company.id)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderLeft: "3px solid",
                      borderColor: isActive ? "success.main" : "divider",
                      backgroundColor: isActive
                        ? "rgba(76, 175, 80, 0.08)"
                        : "background.paper",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: isActive
                          ? `${company.brandColors?.secondary || "#8EA2E8"}25`
                          : "action.hover",
                        transform: isActive
                          ? "translateX(0)"
                          : "translateX(4px)",
                      },
                      "&.Mui-disabled": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      {/* Company Avatar */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: isActive
                            ? "success.main"
                            : "primary.light",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          flexShrink: 0,
                        }}
                      >
                        {isActive ? (
                          <CheckIcon sx={{ fontSize: 18 }} />
                        ) : (
                          company.name.charAt(0).toUpperCase()
                        )}
                      </Box>

                      {/* Company Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 700 : 600}
                          sx={{
                            color: "text.primary",
                            marginBottom: 0.25,
                          }}
                        >
                          {company.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isActive
                              ? company.brandColors?.primary || "primary.main"
                              : "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          {company.roleTitle || company.role}
                        </Typography>
                      </Box>

                      {/* Selected Checkmark */}
                      {isActive && (
                        <CheckIcon
                          sx={{
                            fontSize: 24,
                            color:
                              company.brandColors?.primary || "primary.main",
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
              <Divider sx={{ my: 1 }} />
              <MenuItem
                onClick={handleAddOrganization}
                sx={{
                  py: 1.5,
                  color: "primary.contrastText",
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.main",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AddBusinessIcon
                    fontSize="small"
                    sx={{ color: "primary.contrastText" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Add New Organization"
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            </Menu>

            <Box
              onClick={() => setModalOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 1,
                "&:hover": { backgroundColor: "grey.100" },
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {user?.name}
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "text.secondary" }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Profile Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundImage: "none",
            backgroundColor: "background.default",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "primary.main",
            padding: 3,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "50%",
                padding: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(10px)",
              }}
            >
              <PersonIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ letterSpacing: 0.5 }}
              >
                User Profile
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Manage your account
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              transition: "all 0.3s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          {/* User Name */}
          <Box
            sx={{
              mb: 3,
              textAlign: "center",
              padding: 2.5,
              backgroundColor: "primary.50",
              borderRadius: 2,
              animation: "fadeInScale 0.5s ease-out",
              "@keyframes fadeInScale": {
                from: { opacity: 0, transform: "scale(0.95)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: "text.primary",
                marginBottom: 0.5,
              }}
            >
              {user?.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}{" "}
              Account
            </Typography>
          </Box>

          {/* Info Grid - Responsive */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Email */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
              >
                📧 Email
              </Typography>
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-word", fontWeight: 500 }}
              >
                {user?.email}
              </Typography>
            </Box>

            {/* Last Sign In */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
              >
                🕐 Last Sign In
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatLastSignIn(user?.last_login)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Device Name  */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              💻 Device Name
            </Typography>
            {editingDeviceName ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  size="small"
                  value={deviceNameValue}
                  onChange={(e) => setDeviceNameValue(e.target.value)}
                  sx={{
                    flexGrow: 1,
                    "& .MuiOutlinedInput-root": {
                      transition: "all 0.3s ease",
                      "&:focus-within": {
                        boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
                      },
                    },
                  }}
                  placeholder="e.g., My Laptop"
                  autoFocus
                />
                <IconButton
                  size="small"
                  onClick={handleSaveDeviceName}
                  sx={{
                    backgroundColor: "success.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "success.dark",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelDeviceName}
                  sx={{
                    backgroundColor: "error.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "error.dark",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  backgroundColor: "grey.50",
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
                  {user?.device_name || "Not set"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setEditingDeviceName(true)}
                  sx={{
                    color: "primary.main",
                    "&:hover": { transform: "rotate(20deg)" },
                    transition: "all 0.3s ease",
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Groups */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <GroupIcon fontSize="small" sx={{ color: "primary.main" }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Groups in {activeCompany?.name || "No Company"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
                p: 1.5,
                backgroundColor: "grey.50",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {activeCompany?.groups?.map((group, idx) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  size="small"
                  sx={{
                    animation: `fadeInSlide 0.4s ease-out ${idx * 0.05}s both`,
                    "@keyframes fadeInSlide": {
                      from: { opacity: 0, transform: "translateX(-10px)" },
                      to: { opacity: 1, transform: "translateX(0)" },
                    },
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: "4px",
                    "& .MuiChip-label": {
                      px: 1.5,
                    },
                  }}
                />
              ))}
              {(!activeCompany?.groups ||
                activeCompany.groups.length === 0) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No groups assigned
                  </Typography>
                )}
            </Box>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Companies/Agencies */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <BusinessIcon fontSize="small" sx={{ color: "primary.main" }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Companies / Agencies
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 1,
              }}
            >
              {user?.companies?.map((company, idx) => {
                const isActive = isCompanyActive(company);
                return (
                  <Box
                    key={company.id}
                    onClick={() => !isActive && handleCompanyClick(company.id)}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: isActive ? "3px solid" : "1px solid",
                      borderColor: isActive ? "success.main" : "divider",
                      backgroundColor: isActive
                        ? "rgba(76, 175, 80, 0.08)"
                        : "background.paper",
                      cursor: isActive ? "not-allowed" : "pointer",
                      pointerEvents: isActive ? "none" : "auto",
                      opacity: isActive ? 1 : 0.85,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      overflow: "hidden",
                      animation: `slideInRight 0.4s ease-out ${idx * 0.05}s both`,
                      "@keyframes slideInRight": {
                        from: { opacity: 0, transform: "translateX(-20px)" },
                        to: { opacity: 1, transform: "translateX(0)" },
                      },
                      "&::before": isActive
                        ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background:
                            "linear-gradient(90deg, transparent, #4caf50, transparent)",
                          animation: "shimmer 2s ease-in-out infinite",
                          "@keyframes shimmer": {
                            "0%": { opacity: 0 },
                            "50%": { opacity: 1 },
                            "100%": { opacity: 0 },
                          },
                        }
                        : {},
                      "&:hover": isActive
                        ? {
                          borderColor: "success.main",
                          boxShadow: "0 0px 0px rgba(76, 175, 80, 0)",
                        }
                        : {
                          borderColor: "success.main",
                          boxShadow: "0 6px 24px rgba(76, 175, 80, 0.25)",
                          backgroundColor: "primary.50",
                          transform: "translateY(-3px)",
                        },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: isActive
                            ? "success.main"
                            : "primary.light",
                          color: "white",
                          fontWeight: 700,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {isActive ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          company.name.charAt(0)
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 700 : 600}
                          sx={{
                            color: "text.primary",
                          }}
                        >
                          {company.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {company.roleTitle || company.role}
                        </Typography>
                      </Box>
                      {isActive && (
                        <CheckIcon
                          fontSize="large"
                          sx={{
                            color: "success.main",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 1.5,
                p: 1,
                backgroundColor: "info.lighter",
                borderRadius: 1,
                borderLeft: "3px solid",
                borderColor: "info.main",
                fontStyle: "italic",
              }}
            >
              ℹ️ Click a company to switch
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Organization Wizard */}
      <AddOrganizationWizard
        open={addOrgWizardOpen}
        onClose={() => setAddOrgWizardOpen(false)}
      />
    </>
  );
};

export default Header;
